import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import { WorkingHour } from "./components/WorkingHour";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import EmptyData from "@/components/state-pages/EmptyData";
import { Button } from "@/components/ui/button";
import { Button_Names } from "@/constants/global-constants";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { REPORT_ACTIONS_TOAST } from "@/constants/time-tracking/time-tracking.constant";
import { useSocket } from "@/hooks/useSocket";
import { SocketData } from "@/interfaces/globals";
import { TimeShift } from "@/interfaces/timeTracking";
import {
  timeTrackingApi,
  useGetShiftsQuery,
  useUpdateShiftMutation,
} from "@/redux/api/timeTrackingSettingQuery";
import {
  dateToSec,
  secToDate,
  sortShifts,
} from "@/utils/helper-functions/date-time.helper";
import { tableUpdateToast } from "@/utils/helpers";

const MemoizedWorkingHour = React.memo(WorkingHour);

const SubcontractorShift = ({
  writePermission,
}: {
  writePermission: boolean;
}): React.JSX.Element => {
  const { data: shifts, ...rest } = useGetShiftsQuery("SUBCONTRACTOR");
  const reformat = sortShifts(shifts || []);
  const sortedShifts = reformat?.[0];
  const [shiftData, setShiftData] = useState<TimeShift[]>([]);
  const [initialData, setInitialData] = useState<TimeShift[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [updateShift, { isLoading }] = useUpdateShiftMutation();

  const memoizedSetShiftData = useCallback(setShiftData, []);

  const onSubmit = async (): Promise<void> => {
    try {
      await updateShift({ data: shiftData, shiftCategory: "SUBCONTRACTOR" });
      toast.success(REPORT_ACTIONS_TOAST.SHIFTS_UPDATED_SUCCESS);
    } catch (error) {
      toast.error(REPORT_ACTIONS_TOAST.SHIFTS_UPDATED_FAILED);
    }
  };

  useEffect(() => {
    if (shifts) {
      setShiftData(shifts);
      setInitialData(
        shifts.map((shift) => ({
          ...shift,
          startTime: dateToSec(secToDate(shift?.startTime)),
          endTime: dateToSec(secToDate(shift?.endTime)),
        })),
      );
    }
  }, [shifts]);

  useEffect(() => {
    setIsDirty(JSON.stringify(initialData) !== JSON.stringify(shiftData));
  }, [shiftData, initialData]);

  const { socket } = useSocket("/");
  const dispatch = useDispatch();
  useEffect(() => {
    socket?.on(SOCKET_KEYS.TIME_TRACKING_SHIFTS, (data: SocketData) => {
      dispatch(timeTrackingApi.util.invalidateTags(["shifts"]));
      tableUpdateToast(data, REPORT_ACTIONS_TOAST.SHIFTS_UPDATED_SUCCESS);
    });

    return (): void => {
      socket?.off(SOCKET_KEYS.TIME_TRACKING_SHIFTS);
    };
  }, [socket, rest]);

  return (
    <FetchWrapper {...rest}>
      <form className='flex flex-col w-full gap-4 my-6'>
        {sortedShifts?.length > 0 ? (
          sortedShifts.map((shift, index) => (
            <MemoizedWorkingHour
              disabled={!writePermission}
              key={JSON.stringify(shift) + index}
              shift={shift}
              shiftData={sortedShifts}
              setShiftData={memoizedSetShiftData}
              isDirty={isDirty}
            />
          ))
        ) : (
          <EmptyData title='SUBCONTRACTOR Shift' />
        )}
        {isDirty && (
          <>
            <div className='space-x-4'>
              <Button
                variant={"ghost"}
                type='button'
                onClick={() => setShiftData(initialData)}
                isLoading={isLoading}
                disabled={isLoading || !writePermission}
              >
                {Button_Names.Discard_Change}
              </Button>
              <Button
                className='w-1/4'
                onClick={onSubmit}
                disabled={isLoading || !writePermission}
              >
                {Button_Names.Save_Changes}
              </Button>
            </div>
          </>
        )}
      </form>
    </FetchWrapper>
  );
};

export default SubcontractorShift;
